import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Loader2, Plus, Crown, CheckCircle2, Upload, User as UserIcon } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { CityZipInput } from "@/components/forms/CityZipInput";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import { AddressAutocomplete } from "@/components/forms/AddressAutocomplete";
import { AIDescriptionField } from "@/components/forms/AIDescriptionField";
import { MultiTagSelect } from "@/components/forms/MultiTagSelect";
import { InsurancePicker } from "@/components/forms/InsurancePicker";
import { SERVICES, LANGUAGES } from "@/data/heallexa";
import { useServerFn } from "@tanstack/react-start";
import { createBillingCheckout, getStripePricingFn, type StripePriceInfo } from "@/server/billing.functions";
import { createLifetimeCheckout } from "@/server/lifetime.functions";
import { getProviderPhotoUploadUrl } from "@/server/storage.functions";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { createListingFn } from "./-dashboard.new-listing.functions";

type Plan = "free" | "premium_monthly" | "premium_yearly" | "lifetime_premium";

export const Route = createFileRoute("/dashboard/new-listing")({
 validateSearch: (s: Record<string, unknown>) => ({
 plan: (typeof s.plan === "string" ? s.plan : "free") as Plan,
 }),
 component: NewListingPage,
});

function fmt(cents: number) {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function NewListingPage() {
 const { user, loading } = useAuth();
 const navigate = useNavigate();
 const search = useSearch({ from: "/dashboard/new-listing" });
 const checkout = useServerFn(createBillingCheckout);

 const checkoutLifetime = useServerFn(createLifetimeCheckout);
 const fetchPricing = useServerFn(getStripePricingFn);
 const [prices, setPrices] = useState<StripePriceInfo[] | null>(null);
 // Lifetime is no longer sold — coerce any stale ?plan=lifetime_premium link to Premium Yearly.
 const [plan, setPlan] = useState<Plan>(search.plan === "lifetime_premium" ? "premium_yearly" : (search.plan ?? "free"));
 const [busy, setBusy] = useState(false);
 const [uploadingPhoto, setUploadingPhoto] = useState(false);
 const [form, setForm] = useState<Record<string, any>>({});
 const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

 const isPaid = plan !== "free";

 const handlePhotoUpload = async (file: File) => {
 if (!user) {
 toast.error("Please log in first.");
 return;
 }
 if (!file.type.startsWith("image/")) {
 toast.error("Please upload an image file.");
 return;
 }
 if (file.size > 5 * 1024 * 1024) {
 toast.error("Image must be smaller than 5MB.");
 return;
 }
 setUploadingPhoto(true);
 try {
 const { uploadUrl, publicUrl } = await getProviderPhotoUploadUrl({
 data: { fileName: file.name, contentType: file.type },
 });
 const putRes = await fetch(uploadUrl, {
 method: "PUT",
 headers: { "Content-Type": file.type },
 body: file,
 });
 if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
 set("logo_url", publicUrl);
 toast.success("Photo uploaded.");
 } catch (err: any) {
 toast.error(err?.message ?? "Could not upload photo.");
 } finally {
 setUploadingPhoto(false);
 }
 };

 useEffect(() => {
 if (loading) return;
 if (!user) {
 const signupPlan = plan === "lifetime_premium" ? "lifetime" : plan;
 if (typeof window !== "undefined") {
 localStorage.setItem("heallexa_signup_account_type", "doctor");
 localStorage.setItem("heallexa_post_login_redirect", `/dashboard/new-listing?plan=${plan}`);
 localStorage.setItem("heallexa_selected_plan", plan);
 }
 navigate({ to: "/signup", search: { plan: signupPlan } as any });
 } else {
   fetchPricing().then(setPrices).catch(() => {});
   setForm((f) => (f.email ? f : { ...f, email: user.email ?? "" }));
 }
 }, [user, loading, navigate, plan]);

 const submit = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!user) {
 toast.error("Please log in before creating a listing.");
 navigate({ to: "/login" });
 return;
 }
 const required: Array<[string, string]> = [
 ["first_name", "first name"],
 ["last_name", "last name"],
 ["practice", "practice name"],
 ["specialty", "specialty"],
 ["email", "email"],
 ["phone", "phone"],
 ["npi", "NPI number"],
 ["address", "address"],
 ["city", "city"],
 ];
 for (const [k, label] of required) {
 if (!form[k] || String(form[k]).trim() === "") {
 toast.error(`Please fill in ${label}`);
 return;
 }
 }
 if (!/^\d{10}$/.test(form.npi.trim())) {
 toast.error("NPI must be exactly 10 digits.");
 return;
 }
 if (!form.logo_url) {
 toast.error("Please upload a profile picture for your listing.");
 return;
 }
 setBusy(true);
 try {
 const first_name = form.first_name.trim();
 const last_name = form.last_name.trim();
 const payload = {
 first_name,
 last_name,
 name: `${first_name} ${last_name}`,
 npi: form.npi.trim(),
 practice: form.practice.trim(),
 specialty: form.specialty.trim(),
 phone: form.phone.trim(),
 address: form.address.trim(),
 city: form.city.trim(),
 state: form.state?.trim() || null,
 email: form.email.trim(),
 website: form.website?.trim() || null,
 short_description: form.short_description?.trim() || null,
 about: isPaid ? (form.about?.trim() || null) : null,
 services: isPaid && Array.isArray(form.services) ? form.services : [],
 languages: isPaid && Array.isArray(form.languages) ? form.languages : [],
 insurance: isPaid && Array.isArray(form.insurance) ? form.insurance : [],
 logo_url: form.logo_url || null,
 plan: "free",
 };

 // Ensures the doctor role, inserts the provider (owned by the session
 // user) and records an auto-approved claim — all server-side.
 const inserted = await createListingFn({
 data: {
 name: payload.name,
 first_name: payload.first_name,
 last_name: payload.last_name,
 npi: payload.npi,
 practice: payload.practice,
 specialty: payload.specialty,
 phone: payload.phone,
 address: payload.address,
 city: payload.city,
 state: payload.state,
 email: payload.email,
 website: payload.website,
 short_description: payload.short_description,
 about: payload.about,
 services: payload.services,
 languages: payload.languages,
 insurance: payload.insurance,
 logo_url: payload.logo_url,
 },
 });

 if (!inserted?.id) throw new Error("Could not create listing");

 // Fire-and-forget: confirmation to owner + admin notification
 void invokeEdgeFunction("notify-listing", {
 listing_id: inserted.id,
 notification_type: "listing_created_confirmation",
 event_id: inserted.id,
 });
 void invokeEdgeFunction("notify-listing", {
 listing_id: inserted.id,
 notification_type: "admin_new_listing",
 event_id: inserted.id,
 event_data: {
 specialty: payload.specialty,
 city: payload.city,
 plan: payload.plan,
 owner_email: user.email,
 recipient_override: "contact@heallexa.com",
 },
 });

 if (plan === "free") {
 toast.success("Listing created and published live.");
 navigate({ to: "/dashboard/doctor", search: {} as any });
 return;
 }

 if (plan === "lifetime_premium") {
 const { url } = await checkoutLifetime({
 data: { provider_id: inserted.id, origin: window.location.origin },
 });
 if (url) { window.location.href = url; return; }
 toast.error("Could not start Lifetime checkout, listing saved as Free.");
 navigate({ to: "/dashboard/doctor", search: {} as any });
 return;
 }

 // Premium → kick off Stripe checkout
 const { url } = await checkout({
 data: {
 provider_id: inserted.id,
 price_key: plan,
 origin: window.location.origin,
 },
 });
 if (url) {
 window.location.href = url;
 return;
 }
 toast.error("Could not start checkout, listing saved as Free.");
 navigate({ to: "/dashboard/doctor", search: {} as any });
 } catch (err: any) {
 toast.error(err?.message ?? "Could not create listing. Please try again.");
 } finally {
 setBusy(false);
 }
 };

 if (loading || !user) {
 return (
 <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2">
 <Loader2 className="h-4 w-4 animate-spin" /> Loading…
 </div>
 );
 }

 return (
 <div>

 <header className="pt-28 pb-8 border-b border-border bg-card">
 <div className="mx-auto max-w-3xl px-6">
 <Link to="/dashboard/doctor" search={{} as any} className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2 transition-colors">
 <ArrowLeft className="h-3 w-3" /> Back to dashboard
 </Link>
 <h1 className="text-[22px] font-medium tracking-tight flex items-center gap-2">
 <Plus className="h-5 w-5 text-primary" /> Create your listing
 </h1>
 <p className="text-[13px] text-muted-foreground mt-1">
 Tell us about your practice. You can edit details anytime.
 </p>
 </div>
 </header>

 <main className="mx-auto max-w-3xl px-6 py-10">
 {/* Plan selector */}
 {(() => {
   const monthly = prices?.find((p) => p.key === "premium_monthly");
   const yearly  = prices?.find((p) => p.key === "premium_yearly");
   const moAmt   = monthly?.unit_amount ?? 2900;
   const yrAmt   = yearly?.unit_amount  ?? 9900;
   const savings  = Math.round(((moAmt / 100) * 12 - yrAmt / 100) / ((moAmt / 100) * 12) * 100);
   return (
   <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
     <PlanTile active={plan === "free"} onClick={() => setPlan("free")} title="Free" price="$0" sub="Always free" icon={<CheckCircle2 className="h-4 w-4" />} />
     <PlanTile active={plan === "premium_yearly"} onClick={() => setPlan("premium_yearly")} title="Premium Yearly" price={`${fmt(yrAmt)}/yr`} sub={`Billed annually · Save ${savings}%`} icon={<Crown className="h-4 w-4" />} highlight />
     <PlanTile active={plan === "premium_monthly"} onClick={() => setPlan("premium_monthly")} title="Premium Monthly" price={`${fmt(moAmt)}/mo`} sub="Cancel anytime" icon={<Crown className="h-4 w-4" />} />
   </div>
   );
 })()}

 <form onSubmit={submit} className="card-surface rounded-md p-6 space-y-6">
 {/* Profile picture */}
 <div className="flex items-center gap-4 pb-2 border-b border-border">
 <Avatar className="h-20 w-20 border border-border">
 {form.logo_url ? <AvatarImage src={form.logo_url} alt="Profile" /> : null}
 <AvatarFallback><UserIcon className="h-8 w-8 text-muted-foreground" /></AvatarFallback>
 </Avatar>
 <div className="flex-1">
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Profile picture *</Label>
 <p className="text-xs text-muted-foreground mb-2">
 Required. JPG or PNG, up to 5MB. Shown on your public listing.
 </p>
 <div className="flex items-center gap-2">
 <input
 id="provider-photo-input"
 type="file"
 accept="image/*"
 className="hidden"
 onChange={(e) => {
 const f = e.target.files?.[0];
 if (f) handlePhotoUpload(f);
 e.target.value = "";
 }}
 />
 <Button
 type="button"
 variant="outline"
 size="sm"
 disabled={uploadingPhoto}
 onClick={() => document.getElementById("provider-photo-input")?.click()}
 >
 {uploadingPhoto ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
 {form.logo_url ? "Replace photo" : "Upload photo"}
 </Button>
 {form.logo_url && (
 <Button type="button" variant="ghost" size="sm" onClick={() => set("logo_url", "")}>
 Remove
 </Button>
 )}
 </div>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

 <Field label="First name *" value={form.first_name} onChange={(v) => set("first_name", v)} />
 <Field label="Last name *" value={form.last_name} onChange={(v) => set("last_name", v)} />
 <Field label="Practice name *" value={form.practice} onChange={(v) => set("practice", v)} />
 <div>
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Specialty *</Label>
 <div className="mt-1">
 {/* allowCustom={false}: free text here lands in providers.specialty, the
     taxonomy column. That is how "Chiropractors" and "Family Doctors" ended
     up as raw specialty values alongside the NUCC names. */}
 <SpecialtySelect value={form.specialty ?? ""} onChange={(v) => set("specialty", v)} allowCustom={false} />
 </div>
 </div>
 <Field label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} />
 <Field label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} />
 <Field label="NPI number *" value={form.npi} onChange={(v) => set("npi", v.replace(/\D/g, "").slice(0, 10))} placeholder="10 digits" />
 <Field label="Website" value={form.website} onChange={(v) => set("website", v)} />
 </div>
 <AddressAutocomplete
 address={form.address ?? ""}
 onPick={({ address, city, state, zip }) =>
 setForm((f) => ({
 ...f,
 address,
 city: city ?? f.city,
 state: state ?? f.state,
 zip: zip ?? f.zip,
 }))
 }
 />
 <CityZipInput
 required
 city={form.city ?? ""}
 state={form.state ?? ""}
 zip={form.zip ?? ""}
 onChange={({ city, state, zip }: { city: string; state: string; zip?: string }) => setForm((f) => ({ ...f, city, state, zip }))}
 />
 <AIDescriptionField
 label="Short description (tagline)"
 mode="short"
 value={form.short_description ?? ""}
 onChange={(v) => set("short_description", v)}
 context={{ practice: form.practice, specialty: form.specialty, city: form.city }}
 promptPlaceholder="e.g. friendly family dental, gentle care"
 />

 {isPaid ? (
 <>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Services</Label>
 <div className="mt-1">
 <MultiTagSelect
 value={Array.isArray(form.services) ? form.services : []}
 onChange={(v) => set("services", v)}
 options={SERVICES}
 placeholder="Select services…"
 />
 </div>
 </div>
 <div>
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Languages</Label>
 <div className="mt-1">
 <MultiTagSelect
 value={Array.isArray(form.languages) ? form.languages : []}
 onChange={(v) => set("languages", v)}
 options={LANGUAGES}
 placeholder="Select languages…"
 />
 </div>
 </div>
 <div>
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Insurance accepted</Label>
 <div className="mt-1">
 <InsurancePicker
 value={Array.isArray(form.insurance) ? form.insurance : []}
 onChange={(v) => set("insurance", v)}
 />
 </div>
 </div>
 </div>
 <AIDescriptionField
 label="About"
 mode="about"
 rows={5}
 value={form.about ?? ""}
 onChange={(v) => set("about", v)}
 context={{ practice: form.practice, specialty: form.specialty, city: form.city }}
 promptPlaceholder="e.g. 20 yrs experience, sedation dentistry, kids welcome"
 />
 </>
 ) : (
 <div className="card-surface-flat border-dashed rounded-md p-5 space-y-2">
 <div className="flex items-center gap-2 text-sm font-semibold">
 <Crown className="h-4 w-4 text-primary" /> Premium features
 </div>
 <p className="text-xs text-muted-foreground">
 Services, languages, insurance accepted, full About bio, photos, social links and booking link are unlocked with Premium.
 </p>
 <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
 <li>Services offered</li>
 <li>Languages spoken</li>
 <li>Insurance accepted</li>
 <li>Long-form About / bio</li>
 <li>Photo gallery & social links</li>
 <li>Direct booking link</li>
 </ul>
 <div className="flex gap-2 pt-1">
 <Button type="button" size="sm" variant="outline" onClick={() => setPlan("premium_yearly")}>
 <Crown className="h-3 w-3" /> Upgrade to Premium
 </Button>
 </div>
 </div>
 )}
 <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
 <p className="text-xs text-muted-foreground">
 {plan === "free"
 ? "Your listing will be published as Free."
 : "You'll be redirected to secure checkout to start your Premium subscription."}
 </p>
 <Button type="submit" disabled={busy} size="lg">
 {busy && <Loader2 className="h-4 w-4 animate-spin" />}
 {plan === "free" ? "Create listing" : "Continue to payment"}
 </Button>
 </div>
 </form>
 </main>
 </div>
 );
}

function PlanTile({
 active, onClick, title, price, sub, icon, highlight,
}: { active: boolean; onClick: () => void; title: string; price: string; sub: string; icon: React.ReactNode; highlight?: boolean }) {
 return (
 <button
 type="button"
 onClick={onClick}
 className={`text-left border rounded-md p-4 h-full flex flex-col transition-all hover-lift ${
 active
 ? "border-primary border-t-2 border-t-primary bg-primary/5 shadow-sm"
 : "border-border hover:border-primary/50"
 }`}
 >
 <div className="flex items-center gap-2 text-[13px] font-semibold [&_svg]:text-primary">{icon}{title}</div>
 <div className="mt-1 text-lg font-semibold tracking-tight">{price}</div>
 <div className="mt-auto pt-1 text-[12px] text-muted-foreground">{sub}</div>
 </button>
 );
}

function Field({ label, value, onChange, placeholder, type }: { label: string; value: any; onChange: (v: string) => void; placeholder?: string; type?: string }) {
 return (
 <div>
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</Label>
 <Input type={type ?? "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" />
 </div>
 );
}
